import angular from 'angular'

import templateUrl from './onboarding-policy-inplace-editor.html'
import './onboarding-policy-editor.less'


/**
 * An onboarding policy editor that saves changes to the policy in-place.
 * Editing capabilities are limited to only those changes that can safely be applied
 * while the onboarding policy is in use by an onboarding application.
 */
export const OnboardingPolicyInplaceEditorComponent = {
    controller: OnboardingPolicyInplaceEditorController,
    controllerAs: "vm",
    bindings: {
        onboardingPolicy: "<"
    },
    templateUrl
}

function OnboardingPolicyInplaceEditorController($uibModal, apexApiUrl, onboardingPolicyService, utilityService) {
    'ngInject'
    const vm = this;

    vm.$onInit = onInit;
    vm.onChangeContactEmails = onChangeContactEmails;
    vm.onChangeEnableSelfSignUp = onChangeEnableSelfSignUp;
    vm.onClearBlankForm = onClearBlankForm;
    vm.onClearRequirementsFile = onClearRequirementsFile;
    vm.onEditApplicationStatus = onEditApplicationStatus;
    vm.onEditDocument = onEditDocument;
    vm.onEditDocumentGroup = onEditDocumentGroup;
    vm.onEditReferenceField = onEditReferenceField;
    vm.onMoveApplicationStatus = onMoveApplicationStatus;
    vm.onMoveDocument = onMoveDocument;
    vm.onMoveDocumentGroup = onMoveDocumentGroup;
    vm.onMoveReferenceField = onMoveReferenceField;
    vm.onUploadBlankForm = onUploadBlankForm;
    vm.onUploadRequirementsFile = onUploadRequirementsFile;

    function onInit() {
        vm.statusTypes = [
            "Initial",
            "Hidden",
            "Approved",
            "Terminated",
            "Incomplete",
            "Suspended",
            "SelfSignUp"
        ];
        vm.onboardingPolicy.OnboardingDocuments.forEach(function (document) {
            document.allowedFileTypeOptions = createMultiInputOptions(function () {
                return document.AllowedFileTypes;
            });
        });
        vm.contactEmailOptions = createMultiInputOptions(function () {
            return vm.onboardingPolicy.ContactEmails;
        });
    }

    function onChangeContactEmails() {
        const saved = onboardingPolicyService.updateContactEmails(vm.onboardingPolicy.OnboardingPolicyID,
            vm.onboardingPolicy.ContactEmails)
            .then(function () {
                vm.editForm.$setPristine();
            });

        utilityService.alertOnError(saved, "Save failed");
    }

    function onChangeEnableSelfSignUp() {
        const saved = onboardingPolicyService.patchOnboardingPolicy(vm.onboardingPolicy.OnboardingPolicyID, {
            EnableSelfSignUp: vm.onboardingPolicy.EnableSelfSignUp
        })
            .then(function () {
                vm.editForm.$setPristine();
            });

        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(saved, "Save failed"));
    }

    function onEditApplicationStatus(applicationStatus, index) {
        const isNew = !applicationStatus;
        let editedStatus;
        if (isNew) {
            editedStatus = {
                SortIndex: vm.onboardingPolicy.ApplicationStatuses.length
            };
        } else {
            editedStatus = angular.extend({}, applicationStatus);
            editedStatus.SortIndex = index;
        }
        $uibModal.open({
            component: "apexEditOnboardingPolicyApplicationStatusModal",
            resolve: {
                applicationStatus: function () {
                    return editedStatus;
                },
                index: function () {
                    return index;
                },
                onboardingPolicy: function () {
                    return vm.onboardingPolicy;
                },
                isNew: function () {
                    return isNew;
                },
                statusTypes: function () {
                    // Share the status types list reference to ensure object equivalence for dropdown lists.
                    return vm.statusTypes;
                }
            }
        }).result.then(function (status) {
            if (isNew) {
                const newStatus = {
                    OnboardingApplicationStatusID: status.OnboardingApplicationStatusID,
                    Name: status.Name,
                    StatusType: status.StatusType,
                    Inactive: status.Inactive
                };
                const index = status.SortIndex != null ? status.SortIndex :
                    vm.onboardingPolicy.ApplicationStatuses.length;
                vm.onboardingPolicy.ApplicationStatuses.push(newStatus);
                utilityService.moveItem(vm.onboardingPolicy.ApplicationStatuses, newStatus, index);
            } else {
                // Apply the results to the status instance displayed in the list.
                applicationStatus.Name = status.Name;
                applicationStatus.StatusType = status.StatusType;
                applicationStatus.Inactive = status.Inactive;
            }

        })
    }

    function onEditReferenceField(field, index) {
        const isNew = !field;
        let edited;
        if (isNew) {
            edited = {
                SortIndex: vm.onboardingPolicy.ReferenceFields.length,
                ValueType: "String",
                ShowInGrid: true
            };
        } else {
            edited = angular.extend({}, field);
            edited.SortIndex = index;
        }
        $uibModal.open({
            component: "apexEditOnboardingPolicyReferenceFieldModal",
            resolve: {
                referenceField: function () {
                    return edited;
                },
                applicationStatuses: function () {
                    return vm.onboardingPolicy.ApplicationStatuses;
                },
                index: function () {
                    return index;
                },
                onboardingPolicy: function () {
                    return vm.onboardingPolicy;
                },
                isNew: function () {
                    return isNew;
                }
            }
        }).result.then(function (model) {
            if (isNew) {
                const result = {
                    OnboardingPolicyReferenceFieldID: model.OnboardingPolicyReferenceFieldID
                };
                mapReferenceField(model, result);
                const index = model.SortIndex != null ? model.SortIndex : vm.onboardingPolicy.ReferenceFields.length;
                vm.onboardingPolicy.ReferenceFields.push(result);
                utilityService.moveItem(vm.onboardingPolicy.ReferenceFields, result, index);
            } else {
                // Apply the results to the status instance displayed in the list.
                mapReferenceField(model, field);
            }

        });

        function mapReferenceField(model, field) {
            // Apply the results to the status instance displayed in the list.
            field.Name = model.Name;
            field.RequiredAtStatus = vm.onboardingPolicy.ApplicationStatuses[model.RequiredAtStatusIndex];
            field.IsSensitive = model.IsSensitive;
            field.InputMask = model.InputMask;
            field.ShowInGrid = model.ShowInGrid;
        }
    }

    function onEditDocument(document, index) {
        const isNew = !document;
        let edited;
        if (isNew) {
            edited = {
                SortIndex: vm.onboardingPolicy.OnboardingDocuments.length,
                AllowedFileTypes: []
            };
        } else {
            edited = angular.extend({}, document);
            // The edited version is only shallow copied, so perform a second copy of the file types array.
            edited.AllowedFileTypes = angular.copy(document.AllowedFileTypes);
            edited.SortIndex = index;
        }
        $uibModal.open({
            component: "apexEditOnboardingPolicyDocumentModal",
            resolve: {
                document: function () {
                    return edited;
                },
                documentGroups: function () {
                    return vm.onboardingPolicy.OnboardingDocumentGroups;
                },
                index: function () {
                    return index;
                },
                onboardingPolicy: function () {
                    return vm.onboardingPolicy;
                },
                isNew: function () {
                    return isNew;
                }
            }
        }).result.then(function (model) {
            if (isNew) {
                const result = {
                    OnboardingPolicyDocumentID: model.OnboardingPolicyDocumentID
                };
                mapDocument(model, result);

                // Set default blank form url.
                if (model.BlankFormUpload) {
                    result.BlankFormUrl = getBlankFormUrl(model);
                }

                const index = model.SortIndex != null ? model.SortIndex
                    : vm.onboardingPolicy.OnboardingDocuments.length;
                vm.onboardingPolicy.OnboardingDocuments.push(result);
                utilityService.moveItem(vm.onboardingPolicy.OnboardingDocuments, result, index);
            } else {
                mapDocument(model, document);
            }

        });

        function mapDocument(model, document) {
            document.Name = model.Name;
            document.Description = model.Description;
            document.DocumentGroup = vm.onboardingPolicy.OnboardingDocumentGroups.filter(function (group) {
                return group.OnboardingPolicyDocumentGroupID === model.DocumentGroupID;
            })[0];
            document.IsRequired = model.IsRequired;
            document.UseImageCropper = model.UseImageCropper;
            document.AllowedFileTypes = model.AllowedFileTypes;
        }
    }

    function onEditDocumentGroup(documentGroup, index) {
        const isNew = !documentGroup;
        let edited;
        if (isNew) {
            edited = {
                SortIndex: vm.onboardingPolicy.OnboardingDocumentGroups.length,
                AllowedFileTypes: []
            };
        } else {
            edited = angular.extend({}, documentGroup);
            edited.SortIndex = index;
        }
        $uibModal.open({
            component: "apexEditOnboardingPolicyDocumentGroupModal",
            resolve: {
                documentGroup: function () {
                    return edited;
                },
                index: function () {
                    return index;
                },
                onboardingPolicy: function () {
                    return vm.onboardingPolicy;
                },
                isNew: function () {
                    return isNew;
                }
            }
        }).result.then(function (model) {
            if (isNew) {
                const result = {
                    OnboardingPolicyDocumentGroupID: model.OnboardingPolicyDocumentGroupID
                };
                result.Name = model.Name;

                const index = model.SortIndex != null ? model.SortIndex
                    : vm.onboardingPolicy.OnboardingDocuments.length;

                // Set default requirements file url.
                if (model.RequirementsFileUpload) {
                    result.RequirementsFileUrl = getRequirementsFileUrl(model);
                }
                vm.onboardingPolicy.OnboardingDocumentGroups.push(result);
                utilityService.moveItem(vm.onboardingPolicy.OnboardingDocumentGroups, result, index);
            } else {
                documentGroup.Name = model.Name;
            }
        });
    }

    function onUploadBlankForm(document) {
        const uploaded = onboardingPolicyService.uploadDocumentBlankForm(vm.onboardingPolicy.OnboardingPolicyID,
            document.OnboardingPolicyDocumentID, document.BlankFormUpload);

        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(uploaded, "Upload failed"));

        uploaded.then(function (response) {
            const location = response.headers().location;
            document.BlankFormUpload = null;
            document.BlankFormUrl = location || getBlankFormUrl(document);

            vm.editForm.$setPristine();
        });
    }

    function onClearBlankForm(document) {
        if (!confirm("Are you sure you want to clear this blank form?")) {
            return;
        }

        const cleared = onboardingPolicyService.removeDocumentBlankForm(vm.onboardingPolicy.OnboardingPolicyID,
            document.OnboardingPolicyDocumentID);

        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(cleared, "Clearing file failed"));

        cleared.then(function () {
            // Remove the link to the blank form.
            document.BlankFormUrl = null;

            vm.editForm.$setPristine();
        });
    }

    function onUploadRequirementsFile(group) {
        const file = group.RequirementsFileUpload;
        const uploaded = onboardingPolicyService.uploadDocumentGroupRequirementsFile(
            vm.onboardingPolicy.OnboardingPolicyID, group.OnboardingPolicyDocumentGroupID, file);

        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(uploaded, "Upload failed"));

        uploaded.then(function (response) {
            const location = response.headers().location;
            group.RequirementsFileUpload = null;
            group.RequirementsFileUrl = location || getRequirementsFileUrl(group);

            vm.editForm.$setPristine();
        });
    }

    function onClearRequirementsFile(group) {
        if (!confirm("Are you sure you want to clear this file upload?")) {
            return;
        }

        const cleared = onboardingPolicyService.removeDocumentGroupRequirementsFile(
            vm.onboardingPolicy.OnboardingPolicyID, group.OnboardingPolicyDocumentGroupID);

        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(cleared, "Clearing file failed"));

        cleared.then(function () {
            // Remove the link to the blank form.
            group.RequirementsFileUrl = null;

            vm.editForm.$setPristine();
        });
    }

    function onMoveApplicationStatus(status, index) {
        const id = status.OnboardingApplicationStatusID;
        const saved = onboardingPolicyService.updateApplicationStatus(vm.onboardingPolicy.OnboardingPolicyID,
            id, {
                Name: status.Name,
                StatusType: status.StatusType,
                SortIndex: index
            })
            .then(function () {
                utilityService.moveItem(vm.onboardingPolicy.ApplicationStatuses, status, index);
            });
        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(saved, "Moving application status position failed"));
    }

    function onMoveReferenceField(field, index) {
        const id = field.OnboardingPolicyReferenceFieldID;
        const saved = onboardingPolicyService.updateReferenceField(vm.onboardingPolicy.OnboardingPolicyID,
            id, {
                Name: field.Name,
                RequiredAtStatusIndex: vm.onboardingPolicy.ApplicationStatuses.findIndex(function (status) {
                    return status === field.RequiredAtStatus;
                }),
                IsSensitive: field.IsSensitive,
                InputMask: field.InputMask,
                SortIndex: index
            })
            .then(function () {
                utilityService.moveItem(vm.onboardingPolicy.ReferenceFields, field, index);
            });
        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(saved, "Moving reference ID position failed"));
    }

    function onMoveDocumentGroup(group, index) {
        const id = group.OnboardingPolicyDocumentGroupID;
        const saved = onboardingPolicyService.updateDocumentGroup(vm.onboardingPolicy.OnboardingPolicyID,
            id, {
                Name: group.Name,
                SortIndex: index
            })
            .then(function () {
                utilityService.moveItem(vm.onboardingPolicy.OnboardingDocumentGroups, group, index);
            });
        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(saved, "Moving document group position failed"));
    }

    function onMoveDocument(document, index) {
        const id = document.OnboardingPolicyDocumentID;
        const saved = onboardingPolicyService.updateDocument(vm.onboardingPolicy.OnboardingPolicyID,
            id, {
                Name: document.Name,
                Description: document.Description,
                DocumentGroupID: document.DocumentGroup ? document.DocumentGroup.OnboardingPolicyDocumentGroupID : null,
                IsRequired: document.IsRequired,
                AllowedFileTypes: document.AllowedFileTypes,
                SortIndex: index
            })
            .then(function () {
                utilityService.moveItem(vm.onboardingPolicy.OnboardingDocuments, document, index);
            });
        utilityService.showLoadingUntilResolved(
            utilityService.alertOnError(saved, "Moving document position failed"));
    }

    function getBlankFormUrl(document) {
        // Create URL to the document's blank form. Maybe HATEOAS would be a nicer way of doing this.
        return apexApiUrl + "onboarding-policies/" + vm.onboardingPolicy.OnboardingPolicyID
            + "/documents/" + document.OnboardingPolicyDocumentID + "/blank-form/preview";
    }

    function getRequirementsFileUrl(documentGroup) {
        // Create URL to the document group's requirements file.
        return apexApiUrl + "onboarding-policies/" + vm.onboardingPolicy.OnboardingPolicyID
            + "/document-groups/" + documentGroup.OnboardingPolicyDocumentGroupID + "/requirements-file/preview";
    }

    /**
     * Create kendo-multi-select options for the data source returned from the sourceFn
     * @param {any} sourceFn A function that returns an array representing data selected
     * in the multi-select.
     */
    function createMultiInputOptions(sourceFn) {
        // Return options that perform "server filtering" by enabling the filter
        // and auto-adding the filter text to the list of potential results.
        return {
            filters: "contains",
            dataTextField: "value",
            dataValueField: "value",
            valuePrimitive: true,
            autoClose: false,
            dataSource: {
                transport: {
                    read: function (options) {
                        const results = sourceFn().map(function (item) {
                            return {
                                value: item
                            };
                        });
                        if (options.data && options.data.filter && options.data.filter.filters
                            && options.data.filter.filters[0]) {
                            // Add filter text to the results.
                            results.unshift({
                                value: options.data.filter.filters[0].value
                            });
                        }
                        options.success(results);
                    }
                },
                serverFiltering: true,
                serverPaging: true,
                pageSize: 30
            }
        };
    }
}
